import { useCallback, useMemo } from "react";

import { sdeDescriptors, isAiResultsProject } from "sdecontent";

import Button from "components/Button";
import Icons from "components/Icons";
import Select, { SelectOption } from "components/Select";
import { sampleCode } from "components/hosted-scraping/apiPlaygroundTemplates";

import { useUser } from "routes/dataroutes/UserData";
import { ApiPlaygroundLocationState } from "routes/dashboard/ApiPlaygroundEditDetails";

import { QuickStartStep, useQuickStartContext } from "./QuickStartCard";
import { QuickStartCodeView } from "./QuickStartCodeView";
import { FIXED_WIDTH } from "./constants";
import aiResultsExamples from "./ai-results-examples";
import LanguageSelect from "./LanguageSelect";


const DEFAULT_PROMPT = "What is the largest sun in the universe?";

const aiSdeSelectOptions: SelectOption<string>[] = sdeDescriptors.filter(desc => isAiResultsProject(desc.projectType)).map(desc => { return { name: desc.apiPlayground.dropdownTitle, value: desc.projectType }; });

export default function AiResultsSDEQuickStartSteps() {
  const user = useUser();

  const { programmingLanguage, aiSdeProjectType, setAiSdeProjectType } = useQuickStartContext();

  const selectedSde = useMemo(() => {
    return aiSdeSelectOptions.find(sde => sde.value === aiSdeProjectType) || aiSdeSelectOptions[0];
  }, [ aiSdeProjectType ]);

  const changeSelectedSde = useCallback((sde: SelectOption<string>) => {
    setAiSdeProjectType(sde.value);
  }, [ setAiSdeProjectType ]);

  const codeViewContext: Omit<ApiPlaygroundLocationState, "collectorType"> = useMemo(() => {
    return {
      scrapingMethod: "structured_data_endpoint",
      language: programmingLanguage,
      url: aiResultsExamples[aiSdeProjectType]?.input ?? DEFAULT_PROMPT
    };
  }, [ aiSdeProjectType, programmingLanguage ]);

  const codeExample = useMemo(() => {
    return sampleCode(
      codeViewContext.language,
      codeViewContext.scrapingMethod,
      aiSdeProjectType,
      codeViewContext.url,
      user?.apiKey || "",
      {}
    );
  }, [ user?.apiKey, aiSdeProjectType, codeViewContext.language, codeViewContext.scrapingMethod, codeViewContext.url ]);

  const getApiPlaygroundLocationState = () => {
    return {
      ...codeViewContext,
      collectorType: aiSdeProjectType,
    }
  };


  return (
    <>
      <QuickStartStep
        title="Select your endpoint"
        index={ 2 }
      >
        <Select
          value={ selectedSde }
          onChange={ changeSelectedSde }
          options={ aiSdeSelectOptions }
          size="SM"
          className={ FIXED_WIDTH }
        />
      </QuickStartStep>
      <QuickStartStep
        title="Select your preferred programming language"
        index={ 3 }
      >
        <LanguageSelect />
      </QuickStartStep>
      <QuickStartStep
        title="Try out the snippet"
        index={ 4 }
      >
        <QuickStartCodeView language="python" codeExample={ codeExample } canZoom />
      </QuickStartStep>
      <QuickStartStep
        title="Run request directly in API Playground"
        subtitle="Learn more about all available parameters and credits costs per request."
        icon={ <Icons.APIPlayground className="h-4" /> }
      >
        <Button
          text="Go to playground"
          href="/apiplayground"
          size="SM"
          className="button button-accent"
          navigationState={ getApiPlaygroundLocationState() }
        />
      </QuickStartStep>
    </>
  );
};
